import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ProductionData, HarvestEvent } from '../types';

interface ProductionStore {
  data: ProductionData;
  harvestEvents: HarvestEvent[];
  updateProductionData: (data: Partial<ProductionData>) => void;
  updateHarvestEvent: (index: number, event: Partial<HarvestEvent>) => void;
  calculateFeedConsumed: () => void;
  calculateHarvestTotals: () => { totalCount: number; totalWeight: number; averageWeight: number };
  reset: () => void;
}

const initialProductionData: ProductionData = {
  placementDate: '',
  harvestDate: '',
  cycleDaysManual: 0,
  chickCount: 0,
  feedIncoming: 0,
  feedRemaining: 0,
  feedConsumed: 0,
};

const initialHarvestEvents: HarvestEvent[] = [
  { type: 'thinning', count: 0, averageWeight: 0, totalWeight: 0 },
  { type: 'final', count: 0, averageWeight: 0, totalWeight: 0 },
];

export const useProductionStore = create<ProductionStore>()(
  persist(
    (set, get) => ({
      data: initialProductionData,
      harvestEvents: initialHarvestEvents,

      updateProductionData: (newData) => {
        set((state) => ({
          data: { ...state.data, ...newData },
        }));
        
        // Auto-calculate feed consumed if both values are present
        const { feedIncoming, feedRemaining } = get().data;
        if (feedIncoming > 0 && feedRemaining >= 0) {
          get().calculateFeedConsumed();
        }
      },

      updateHarvestEvent: (index, event) => {
        set((state) => {
          const newEvents = [...state.harvestEvents];
          newEvents[index] = { ...newEvents[index], ...event };
          
          // Smart calculation: if 2 of 3 values are present, calculate the third
          const evt = newEvents[index];
          if (evt.count > 0 && evt.averageWeight > 0 && !event.totalWeight) {
            evt.totalWeight = evt.count * evt.averageWeight;
          } else if (evt.count > 0 && evt.totalWeight > 0 && !event.averageWeight) {
            evt.averageWeight = evt.totalWeight / evt.count;
          } else if (evt.averageWeight > 0 && evt.totalWeight > 0 && !event.count) {
            evt.count = Math.round(evt.totalWeight / evt.averageWeight);
          }
          
          return { harvestEvents: newEvents };
        });
      },

      calculateFeedConsumed: () => {
        set((state) => {
          const consumed = state.data.feedIncoming - state.data.feedRemaining;
          return {
            data: {
              ...state.data,
              feedConsumed: consumed > 0 ? consumed : 0,
            },
          };
        });
      },

      calculateHarvestTotals: () => {
        const { harvestEvents } = get();
        
        const totalCount = harvestEvents.reduce((sum, evt) => sum + evt.count, 0);
        const totalWeight = harvestEvents.reduce((sum, evt) => sum + evt.totalWeight, 0);
        const averageWeight = totalCount > 0 ? totalWeight / totalCount : 0;
        
        return { totalCount, totalWeight, averageWeight };
      },

      reset: () => {
        set({
          data: initialProductionData,
          harvestEvents: initialHarvestEvents,
        });
      },
    }),
    {
      name: 'broiler_production_data',
      partialize: (state) => ({
        data: state.data,
        harvestEvents: state.harvestEvents,
      }),
    }
  )
);
